import React from 'react';
import { Modal } from '../../../components/common/Modal';
import { CreateSampleModal } from '../../../components/inventory/SampleModal';
import { InventoryHistoryDialog } from '../InventoryHistoryDialog';
import { InventoryRoastProfileModal } from '../../../components/inventory/InventoryRoastProfileModal';
import { GreenBeanProfileForm } from '../../purchasing/GreenBeanProfileForm';
import { CuppingProfileForm } from '../CuppingProfileForm';
import type { CuppingProfile } from '../../../services/profileService';
import type { GreenBeanProfile } from '../../../types/greenBean';
import type { InventoryItem, InventoryLot, InventorySample } from '../../../types/inventory';

interface RoastModalContext {
  referenceId: string;
  label: string;
  readOnly?: boolean;
}

interface QCModalState {
  isOpen: boolean;
  type: 'bean' | 'cupping';
  entityId: string;
  entityType: 'LotType' | 'SampleType' | 'ItemType';
  lotId?: string;
  readOnly?: boolean;
}

interface InventoryItemDetailsModalsProps {
  itemId: string;
  itemType: InventoryItem['itemType'];
  itemName?: string;
  workOrderReferenceId?: string;
  isCreateSampleOpen: boolean;
  onCloseCreateSample: () => void;
  onCreateSample: (sample: InventorySample) => void;
  qcModal: QCModalState;
  onCloseQcModal: () => void;
  getInitialQCData: () => Partial<GreenBeanProfile> | Partial<CuppingProfile> | undefined;
  onQcSuccess: () => void;
  onRefetchGreenBeans: () => void;
  onRefetchCupping: () => void;
  isHistoryOpen: boolean;
  onCloseHistory: () => void;
  selectedLotId?: string;
  selectedLotNumber?: string;
  lots: InventoryLot[];
  onHistoryUpdate: () => void;
  roastModalContext: RoastModalContext | null;
  onCloseRoastModal: () => void;
  onRoastSaved: () => void;
  canWrite?: boolean;
}

export const InventoryItemDetailsModals: React.FC<InventoryItemDetailsModalsProps> = ({
  itemId,
  itemType,
  itemName,
  workOrderReferenceId,
  isCreateSampleOpen,
  onCloseCreateSample,
  onCreateSample,
  qcModal,
  onCloseQcModal,
  getInitialQCData,
  onQcSuccess,
  onRefetchGreenBeans,
  onRefetchCupping,
  isHistoryOpen,
  onCloseHistory,
  selectedLotId,
  selectedLotNumber,
  lots,
  onHistoryUpdate,
  roastModalContext,
  onCloseRoastModal,
  onRoastSaved,
  canWrite = true,
}) => {
  return (
    <>
      <CreateSampleModal
        isOpen={isCreateSampleOpen}
        onClose={onCloseCreateSample}
        onCreate={onCreateSample}
        itemId={itemId}
        readOnly={!canWrite}
      />

      {qcModal.isOpen && (
        <Modal
          isOpen={qcModal.isOpen}
          onClose={onCloseQcModal}
          title={qcModal.type === 'bean' ? 'Green Bean QC' : 'Cupping QC'}
          maxWidth={qcModal.type === 'bean' ? '4xl' : '6xl'}
        >
          {qcModal.type === 'bean' ? (
            <GreenBeanProfileForm
              entityType={qcModal.entityType}
              entityId={qcModal.entityId}
              initialData={getInitialQCData() as Partial<GreenBeanProfile>}
              readOnly={qcModal.readOnly}
              onSuccess={() => {
                onQcSuccess();
                onRefetchGreenBeans();
              }}
              onCancel={onCloseQcModal}
            />
          ) : (
            <CuppingProfileForm
              entityType={qcModal.entityType}
              entityId={qcModal.entityId}
              lotId={qcModal.lotId}
              initialData={getInitialQCData() as Partial<CuppingProfile>}
              readOnly={qcModal.readOnly}
              onSuccess={() => {
                onQcSuccess();
                onRefetchCupping();
              }}
              onCancel={onCloseQcModal}
            />
          )}
        </Modal>
      )}

      <InventoryHistoryDialog
        isOpen={isHistoryOpen}
        onClose={onCloseHistory}
        itemId={itemId}
        itemType={itemType}
        roastedWorkOrderReferenceId={workOrderReferenceId}
        finishedGoodName={itemName}
        lotId={selectedLotId}
        lotNumber={selectedLotNumber}
        currentStock={lots.find(lot => lot.id === selectedLotId)?.remainingQuantity}
        onUpdate={onHistoryUpdate}
        readOnly={!canWrite}
      />

      {roastModalContext && (
        <InventoryRoastProfileModal
          isOpen={!!roastModalContext}
          onClose={onCloseRoastModal}
          title={roastModalContext.label}
          referenceId={roastModalContext.referenceId}
          readOnly={roastModalContext.readOnly}
          onSaved={onRoastSaved}
        />
      )}
    </>
  );
};
